/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import styled from "styled-components";
import Label from "../Label";
import Icon from "../Icon";

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: start;
  gap: 8px;
`;

const CheckboxInputWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const CheckboxIcon = styled((props) => <Icon.Check {...props} />)`
  ${(props) => props.$checked && "display: block;"}
  ${(props) => !props.$checked && "display: none;"}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  pointer-events: none;
`;

const CheckboxInput = styled((props) => <input {...props} />)`
  width: 24px;
  height: 24px;
  margin: 0;
  border: 1px solid rgba(${({ theme }) => theme.colors.accent.rgba});
  border-radius: 4px;
  ${(props) => props.$error && `border-color: rgba(${props.theme.colors.error.rgba});`}
  background: ${(props) =>
    props.$checked
      ? `rgba(${props.theme.colors.accent.rgb}, 0.1)`
      : `rgba(${props.theme.colors.background.rgba})`};
  appearance: none;
  cursor: pointer;
  ${(props) => props.$disabled && "opacity: 0.2; cursor: not-allowed;"}
`;

const CheckboxLabel = styled((props) => <Label {...props} />)`
  cursor: pointer;
`;

const CheckboxUncontrolled = ({
  name,
  children,
  label,
  required,
  onChange,
  defaultValue,
  disableLabelClick,
  ...props
}) => {
  const [checkedState, setCheckedState] = useState(defaultValue || false);

  const handleChange = (e) => {
    setCheckedState(e.target.checked);

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <CheckboxWrapper>
      <CheckboxInputWrapper>
        <CheckboxInput
          id={name}
          type="checkbox"
          aria-labelledby={disableLabelClick ? `${name}-label` : undefined}
          $checked={checkedState}
          $disabled={props.disabled}
          onChange={handleChange}
          {...props}
        />
        <CheckboxIcon $checked={checkedState} size={Icon.SIZE.SM} />
      </CheckboxInputWrapper>
      {children && (
        <CheckboxLabel id={disableLabelClick ? `${name}-label` : undefined} name={name} required={required} disableLabelClick={disableLabelClick}>
          {label ? label : children}
        </CheckboxLabel>
      )}
    </CheckboxWrapper>
  );
};

export default CheckboxUncontrolled;
